import React from 'react';
import {
  IconDots,
  IconDownload,
  IconHistory,
  IconTrash,
} from '@tabler/icons-react';
import { Box } from 'src/design/atoms/Box';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { CloseButton } from 'src/design/atoms/CloseButton';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { DeleteButton } from 'src/design/atoms/DeleteButton';
import { Divider } from 'src/design/atoms/Divider';
import { EmptyState } from 'src/design/atoms/EmptyState';
import { Group } from 'src/design/atoms/Group';
import { ScrollArea } from 'src/design/atoms/ScrollArea';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { ThemeIcon } from 'src/design/atoms/ThemeIcon';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { AccessIndicator } from 'src/design/molecules/AccessIndicator';
import { InlineTextInput } from 'src/design/molecules/InlineTextInput';
import { Menu } from 'src/design/molecules/Menu';
import { Modal } from 'src/design/molecules/Modal';
import { PropertyList } from 'src/design/molecules/PropertyList';
import { Tabs } from 'src/design/molecules/Tabs';
import { Timeline } from 'src/design/molecules/Timeline';
import { Dropzone } from 'src/design/organisms/Dropzone';
import { DocumentDto, FileParameter } from 'src/api/FiretrolApiClient';
import {
  useDeleteDocument,
  useUpdateDocument,
  useUploadDocumentVersion,
} from 'src/api/queries/documents/useDocumentMutations';
import { useDownloadDocument } from 'src/api/queries/documents/useDocumentDownload';
import {
  accessKindForDocument,
  userDisplayName,
} from 'src/app/documents/documentAccess';
import { fileIcon } from 'src/app/documents/fileIcons';
import { useAuth } from 'src/hooks/useAuth';
import {
  FIELD_LIMITS,
  formatDate,
  formatDateTime,
  humanFileSize,
} from 'src/utils';
import { VersionList } from './VersionList';

export interface IDocumentDetailsProps {
  jobId: number;
  document: DocumentDto;
  canDelete: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const DocumentDetails: React.FC<IDocumentDetailsProps> = ({
  jobId,
  document: doc,
  canDelete,
  onClose,
  onDeleted,
}) => {
  const { user } = useAuth();
  const [tab, setTab] = React.useState<string | null>('details');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const openFilePicker = React.useRef<() => void>(null);

  const updateDocument = useUpdateDocument(jobId);
  const uploadVersion = useUploadDocumentVersion(jobId);
  const deleteDocument = useDeleteDocument(jobId);
  const downloadDocument = useDownloadDocument();

  const currentUserId = user?.id ?? -1;
  const access = accessKindForDocument(doc, currentUserId);
  const typeLabel = doc.type?.toUpperCase() || 'FILE';

  // InlineTextInput only leaves edit mode when its save resolves, so a failed
  // rename has to reject or the user's unsaved text disappears.
  const updateDoc = async (
    changes: Partial<Pick<DocumentDto, 'name' | 'description'>>,
  ) => {
    await updateDocument.mutateAsync({ documentId: doc.id, request: changes });
  };

  const handleVersionDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    const fileParam: FileParameter = { data: file, fileName: file.name };
    uploadVersion.mutate({ documentId: doc.id, file: fileParam });
  };

  const handleDelete = async () => {
    await deleteDocument.mutateAsync(doc.id);
    setConfirmDeleteOpen(false);
    onDeleted();
  };

  const deleteMenuItem = (
    <Menu.Item
      leftSection={<IconTrash size={16} />}
      color="red"
      disabled={!canDelete}
      onClick={() => setConfirmDeleteOpen(true)}
    >
      Delete document
    </Menu.Item>
  );

  return (
    <ScrollArea h="100%">
      <Stack gap="md" p="md">
        <Group align="flex-start" gap="xs" wrap="nowrap">
          <ThemeIcon variant="light" color="gray" size="lg">
            {fileIcon(doc.type)}
          </ThemeIcon>
          <InlineTextInput
            inline
            initialValue={doc.name ?? ''}
            maxLength={FIELD_LIMITS.singleLine}
            onSave={(value) => updateDoc({ name: value })}
            containerProps={{ flex: 1 }}
            textProps={{ size: 'sm', fw: 600, c: 'bright' }}
          />
          <CloseButton aria-label="Close details" onClick={onClose} />
        </Group>

        <Text size="xs" c="dimmed">
          {[
            typeLabel,
            humanFileSize(doc.size),
            `Modified ${formatDate(doc.updatedAt, 'MMM D, YYYY')}`,
          ].join(' · ')}
        </Text>

        {/* File picker for "New version": hidden from layout, still clickable
            through openRef. */}
        <Dropzone
          display="none"
          unstyled
          activateOnClick={false}
          useFsAccessApi={false}
          multiple={false}
          maxFiles={1}
          openRef={openFilePicker}
          onDrop={handleVersionDrop}
        >
          <span />
        </Dropzone>

        <Group gap="xs" wrap="nowrap">
          <CreateButton
            flex={1}
            size="xs"
            leftSection={<IconDownload size={16} />}
            onClick={() =>
              downloadDocument.mutate({ jobId, documentId: doc.id })
            }
          >
            Download
          </CreateButton>
          <SecondaryButton
            flex={1}
            size="xs"
            leftSection={<IconHistory size={16} />}
            onClick={() => openFilePicker.current?.()}
          >
            New version
          </SecondaryButton>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <BasicActionIcon variant="subtle" label="More actions">
                <IconDots size={16} />
              </BasicActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {canDelete ? (
                deleteMenuItem
              ) : (
                <Tooltip label="System documents cannot be deleted">
                  <Box>{deleteMenuItem}</Box>
                </Tooltip>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>

        <AccessIndicator kind={access.kind} people={access.people} />

        <Divider />

        <Tabs value={tab} onChange={setTab} variant="default">
          <Tabs.List grow>
            <Tabs.Tab value="details">Details</Tabs.Tab>
            <Tabs.Tab value="versions">
              {`Versions (${doc.versions.length})`}
            </Tabs.Tab>
            <Tabs.Tab value="activity">
              {`Activity (${doc.activity.length})`}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="details" pt="md">
            <Stack gap="sm">
              <PropertyList
                size="xs"
                items={[
                  {
                    label: 'Type',
                    value: typeLabel,
                  },
                  { label: 'Size', value: humanFileSize(doc.size) },
                  {
                    label: 'Owner',
                    value: userDisplayName(
                      doc.uploadedById,
                      doc.uploadedBy?.fullName,
                      currentUserId,
                    ),
                  },
                  {
                    label: 'Created',
                    value: formatDate(doc.createdAt, 'MMM D, YYYY'),
                  },
                  {
                    label: 'Modified',
                    value: formatDate(doc.updatedAt, 'MMM D, YYYY'),
                  },
                ]}
              />

              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                  Description
                </Text>
                <InlineTextInput
                  initialValue={doc.description ?? ''}
                  maxLength={FIELD_LIMITS.narrative}
                  onSave={(value) => updateDoc({ description: value })}
                  emptyText="Add a description"
                  textProps={{ size: 'sm', c: 'bright' }}
                />
              </Box>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="versions" pt="md">
            <VersionList
              jobId={jobId}
              versions={doc.versions}
              currentUserId={user?.id}
            />
          </Tabs.Panel>

          <Tabs.Panel value="activity" pt="md">
            {doc.activity.length === 0 ? (
              <EmptyState description="No activity yet" />
            ) : (
              <Timeline bulletSize={18} lineWidth={1}>
                {doc.activity.map((activity) => (
                  <Timeline.Item key={activity.id}>
                    <Text size="sm" c="bright">
                      {`${userDisplayName(
                        activity.userId,
                        activity.user?.fullName,
                        currentUserId,
                      )} ${activity.action ?? ''}`}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatDateTime(
                        activity.createdAt,
                        'MMM D, YYYY · h:mm A',
                      )}
                    </Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <Modal
        opened={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title="Delete document"
        size="sm"
        actions={[
          <DeleteButton
            key="delete"
            onClick={handleDelete}
            loading={deleteDocument.isPending}
          >
            Delete
          </DeleteButton>,
        ]}
      >
        <Text>
          {`Delete "${doc.name ?? 'this document'}"? This cannot be undone.`}
        </Text>
      </Modal>
    </ScrollArea>
  );
};
